// Object Explorer reveal for navigational-features Phase 1.
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 5 (table/view) +
// Slice 6 (column reuses the schema reveal) + Slice 9 (routine).
//
// `findRevealNodeId` is the pure lookup; `navigateToObjectExplorer` reads
// the live OE tree, finds the matching node, and sets selection + highlight
// on the UI store. The "current SchemaSnapshot" the slice references is
// represented here by the OE tree — Slice 5 doesn't introduce a per-Monaco
// SqlResolverService (Slices 6+ may).

import type { DatabaseExplorerNode } from '../../explorer/models/DatabaseTreeModels';
import { useDatabaseStore } from '../../database-objects/store/databaseStore';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { createDatabaseObjectNodeId } from '../../explorer/utils/nodeIdUtils';
import type { DatabaseEngineId } from '@/core/engines';
import type { ExternalObjectKind, QualifiedName } from '../types';

/** OE-tree object-types we navigate to. Mirrors `DatabaseObjectType` from
 *  `databaseObjectTypes.ts` but narrowed to the kinds Phase 1 emits. */
export type RevealObjectType =
  | 'table'
  | 'view'
  | 'stored-procedure'
  | 'function';

export type NavigationTarget = {
  readonly objectKind: ExternalObjectKind;
  readonly qualified: QualifiedName;
  readonly engineId: DatabaseEngineId;
  readonly connectionId: string;
  /** Active database for the editor that fired the F12. When unset, the
   *  function falls back to the engine default and the lookup is best-effort. */
  readonly database?: string;
};

export type NavigationResult = {
  readonly revealed: boolean;
  readonly nodeId?: string;
};

/** Returns the engine's default schema name when an unqualified identifier
 *  needs one to address an OE node. Mirrors the same defaults used by the
 *  shared `viewDependenciesAction` (designActions.ts:124). */
export function defaultSchemaForEngine(engineId: DatabaseEngineId): string {
  if (engineId === 'pgsql') return 'public';
  if (engineId === 'sqlite') return '';
  // mssql / mysql / oracle / mock all default to 'dbo' for Phase 1 — matches
  // the shared dependency-viewer fallback. Per-dialect refinement (mysql's
  // database-as-schema, oracle's user-as-schema) is a future slice.
  return 'dbo';
}

/** Translates the navigational-features `ExternalObjectKind` to the OE tree's
 *  `DatabaseObjectType` so node IDs match what `addDatabaseObjects` produces. */
export function objectKindToTreeType(kind: ExternalObjectKind): RevealObjectType | null {
  if (kind === 'table') return 'table';
  if (kind === 'view') return 'view';
  if (kind === 'routine') return 'stored-procedure';
  // 'column' is not a top-level OE node — Slice 6 navigates to the table
  // node and then focuses the column row inside Table Designer.
  return null;
}

/** Pure lookup. Walks the OE tree to find a node ID matching `target`. The
 *  walk is folded-name aware so unquoted identifiers in the editor match
 *  case-different node names (`USERS` cursor → `users` OE node on case-
 *  insensitive engines like mssql). */
export function findRevealNodeId(
  treeData: readonly DatabaseExplorerNode[],
  target: NavigationTarget,
): string | null {
  const treeType = objectKindToTreeType(target.objectKind);
  if (!treeType) return null;

  const database = pickDatabase(target);
  const schema = pickSchema(target);
  const name = target.qualified.name;

  const candidates = collectObjectCandidates(treeData, target.connectionId, treeType);
  // Folded-name match: prefer exact-case, fall back to case-insensitive so
  // a folded-name from the resolver still hits an OE node on a case-
  // sensitive engine that happens to store mixed-case identifiers.
  const matches = candidates.filter((c) => {
    if (database && c.database && c.database !== database) return false;
    if (schema && c.schema && !schemasMatch(c.schema, schema)) return false;
    return objectNamesMatch(c.name, name, target.qualified.foldedName);
  });
  if (matches.length === 0) return null;

  const exact = matches.find((c) => c.schema === schema && c.name === name);
  const chosen = exact ?? matches[0];
  return createDatabaseObjectNodeId(
    target.connectionId,
    chosen.database ?? database,
    treeType,
    chosen.schema ?? schema,
    chosen.name,
  );
}

/** Read-only probe: would `navigateToObjectExplorer(target)` find a node in
 *  the live OE tree? The DefinitionProvider asks this (it must stay side-
 *  effect free — Monaco also calls it for the Ctrl/Cmd+hover link probe). */
export function canRevealInObjectExplorer(target: NavigationTarget): boolean {
  return findRevealNodeId(useDatabaseStore.getState().treeData, target) !== null;
}

/** Side-effecting reveal. Reads the live OE tree, finds the matching node,
 *  then sets the selection + highlight on the UI store so the existing
 *  Object-Explorer UI scrolls + flashes the row. Returns whether navigation
 *  was actually performed. Runs from the Go to Definition editor opener
 *  (`definitionNavigationOpener.ts`), never from the provider. */
export function navigateToObjectExplorer(target: NavigationTarget): NavigationResult {
  const treeData = useDatabaseStore.getState().treeData;
  const nodeId = findRevealNodeId(treeData, target);
  if (!nodeId) return { revealed: false };

  const ui = useDatabaseUIStore.getState();
  ui.setSelectedNodeId(nodeId);
  // Highlight pulses the row + scrolls into view. The UI clears it on the
  // next selection change automatically; we don't need to toggle it back.
  ui.setHighlightedNodeId(nodeId);
  return { revealed: true, nodeId };
}

// ─── Internal helpers ──────────────────────────────────────────────────────

function pickDatabase(target: NavigationTarget): string | undefined {
  const fromQn = target.qualified.database;
  if (fromQn) return fromQn;
  return target.database;
}

function pickSchema(target: NavigationTarget): string {
  const fromQn = target.qualified.schema;
  if (fromQn) return fromQn;
  return defaultSchemaForEngine(target.engineId);
}

type ObjectCandidate = {
  readonly database: string | undefined;
  readonly schema: string | undefined;
  readonly name: string;
};

function collectObjectCandidates(
  treeData: readonly DatabaseExplorerNode[],
  connectionId: string,
  treeType: RevealObjectType,
): ObjectCandidate[] {
  const out: ObjectCandidate[] = [];
  visit(treeData, undefined);
  return out;

  function visit(
    nodes: readonly DatabaseExplorerNode[] | undefined,
    currentDatabase: string | undefined,
  ): void {
    if (!nodes) return;
    for (const node of nodes) {
      if (node.type === 'connection') {
        const meta = node.metadata as { id?: string } | undefined;
        if (meta?.id !== connectionId) continue;
        visit(node.children as readonly DatabaseExplorerNode[] | undefined, undefined);
        continue;
      }
      if (node.type === 'database') {
        const meta = node.metadata as { name?: string } | undefined;
        const dbName = meta?.name;
        visit(node.children as readonly DatabaseExplorerNode[] | undefined, dbName);
        continue;
      }
      if (node.type === treeType) {
        const meta = node.metadata as { objectDetails?: { name?: string; schema?: string; connectionId?: string } } | undefined;
        const obj = meta?.objectDetails;
        if (!obj?.name) continue;
        if (obj.connectionId && obj.connectionId !== connectionId) continue;
        out.push({ database: currentDatabase, schema: obj.schema, name: obj.name });
        continue;
      }
      visit(node.children as readonly DatabaseExplorerNode[] | undefined, currentDatabase);
    }
  }
}

function schemasMatch(treeSchema: string, target: string): boolean {
  if (treeSchema === target) return true;
  return treeSchema.toLowerCase() === target.toLowerCase();
}

function objectNamesMatch(
  treeName: string,
  refName: string,
  refFolded: string,
): boolean {
  if (treeName === refName) return true;
  if (treeName.toLowerCase() === refFolded.toLowerCase()) return true;
  return treeName.toLowerCase() === refName.toLowerCase();
}
